import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Award, Bike, CalendarDays, ChevronRight, MountainIcon, Sunrise, Zap } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { BottomNav } from "@/components/BottomNav";
import { MODALITIES, relativeMinutes, type Modality } from "@/lib/modality";

export const Route = createFileRoute("/profile_/$userId")({
  component: PublicProfilePage,
});

interface Profile {
  id: string;
  nickname: string;
  avatar_url: string | null;
  preferred_modality: Modality | null;
  preferred_pace: string | null;
  watts_points: number | null;
}
interface RecentRide { id: string; modality: Modality; start_time: string; estimated_km: number | null; start_address: string | null; }

function PublicProfilePage() {
  const { userId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [stats, setStats] = useState({ created: 0, joined: 0, following: 0, followers: 0 });
  const [recent, setRecent] = useState<RecentRide[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) navigate({ to: "/auth" }); }, [loading, user, navigate]);

  // Redirect to own profile if same user
  useEffect(() => {
    if (user && user.id === userId) navigate({ to: "/profile", replace: true });
  }, [user, userId, navigate]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      const { data: p } = await supabase.from("profiles")
        .select("id, nickname, avatar_url, preferred_modality, preferred_pace, watts_points")
        .eq("id", userId).single();
      if (p) setProfile(p as Profile);

      const [{ count: following }, { count: followers }, { data: creatorRides }, { data: participantRows }] = await Promise.all([
        supabase.from("follows").select("*", { count: "exact", head: true }).eq("follower_id", userId),
        supabase.from("follows").select("*", { count: "exact", head: true }).eq("following_id", userId),
        supabase.from("rides").select("id").eq("creator_id", userId),
        supabase.from("ride_participants").select("ride_id").eq("user_id", userId),
      ]);
      const creatorRideIds = new Set((creatorRides || []).map((r: any) => r.id));
      const joinedCount = (participantRows || []).filter((p: any) => !creatorRideIds.has(p.ride_id)).length;
      setStats({
        created: (creatorRides || []).length,
        joined: joinedCount,
        following: following ?? 0,
        followers: followers ?? 0,
      });

      const { data: rs } = await supabase.from("rides")
        .select("id, modality, start_time, estimated_km, start_address")
        .eq("creator_id", userId)
        .order("start_time", { ascending: false }).limit(5);
      setRecent((rs as RecentRide[]) || []);

      if (user) {
        const { data: f } = await supabase.from("follows")
          .select("follower_id")
          .eq("follower_id", user.id).eq("following_id", userId).maybeSingle();
        setIsFollowing(!!f);
      }
    })();
  }, [userId, user]);

  const toggleFollow = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      if (isFollowing) {
        const { error } = await supabase.from("follows")
          .delete().match({ follower_id: user.id, following_id: userId });
        if (error) throw error;
        setIsFollowing(false);
        setStats((s) => ({ ...s, followers: Math.max(0, s.followers - 1) }));
      } else {
        const { error } = await supabase.from("follows")
          .insert({ follower_id: user.id, following_id: userId });
        if (error) throw error;
        setIsFollowing(true);
        setStats((s) => ({ ...s, followers: s.followers + 1 }));
      }
    } catch (err: any) {
      toast.error(err.message || "No se pudo completar la acción.");
    } finally { setBusy(false); }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-background p-5 pb-28 space-y-5">
        <div className="h-8 w-24 animate-pulse rounded-full bg-surface-elevated" />
        <div className="h-20 w-20 animate-pulse rounded-full bg-surface-elevated" />
        <div className="h-20 w-full animate-pulse rounded-2xl bg-surface-elevated" />
      </div>
    );
  }

  const mLabel = MODALITIES.find((m) => m.value === profile.preferred_modality)?.label;

  return (
    <div className="overflow-y-auto bg-background fade-in" style={{ height: '100dvh', paddingBottom: 80 }}>
      <header className="relative flex items-center justify-between px-5" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 8 }}>
        <button onClick={() => navigate({ to: "/map" })} className="rounded-full bg-surface p-2" aria-label="Volver">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <button
          onClick={toggleFollow}
          disabled={busy}
          className={`rounded-full px-4 py-2 text-sm transition-colors ${
            isFollowing
              ? "border border-[#2a2a2a] text-white"
              : "bg-[#f5e642] text-black font-bold"
          }`}
        >
          {isFollowing ? "Siguiendo ✓" : "Seguir"}
        </button>
      </header>

      <div className="px-5">
        <div className="flex items-center" style={{ gap: 16 }}>
          <div className="relative shrink-0">
            <div className="h-20 w-20 overflow-hidden rounded-full bg-primary font-bold text-primary-foreground ring-2 ring-primary/40">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-3xl">
                  {profile.nickname[0]?.toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col justify-center min-w-0 flex-1">
            <div className="font-display text-2xl truncate">@{profile.nickname}</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {mLabel && (
                <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-[11px] font-medium">{mLabel}</span>
              )}
              {profile.preferred_pace && (
                <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-[11px] font-medium capitalize">{profile.preferred_pace}</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_1px_1fr_1px_1fr_1px_1fr] rounded-2xl border border-border bg-surface" style={{ marginTop: 16 }}>
          <Stat n={stats.created} label="Creadas" />
          <div className="h-8 w-px bg-[#2a2a2a] self-center" />
          <Stat n={stats.joined} label="Unidas" />
          <div className="h-8 w-px bg-[#2a2a2a] self-center" />
          <Stat n={stats.following} label="Siguiendo" />
          <div className="h-8 w-px bg-[#2a2a2a] self-center" />
          <Stat n={stats.followers} label="Seguidores" />
        </div>

        <div className="mt-6 space-y-3">
          <Widget icon={<Award className="h-4 w-4 text-[#F5C518]" />} title="Badges">
            <div className="grid grid-cols-3 gap-2">
              {[
                { Icon: Bike, label: "Primera salida" },
                { Icon: Sunrise, label: "Madrugador" },
                { Icon: MountainIcon, label: "Climber" },
              ].map((b) => (
                <div key={b.label} className="rounded-xl bg-[#222] p-3 text-center">
                  <b.Icon className="mx-auto mb-1 h-5 w-5 text-primary" strokeWidth={1.8} />
                  <div className="text-[11px] text-white/80">{b.label}</div>
                </div>
              ))}
            </div>
          </Widget>

          <Widget icon={<CalendarDays className="h-4 w-4 text-[#F5C518]" />} title="Salidas Recientes">
            {recent.length === 0 ? (
              <p className="py-2 text-center text-sm text-white/60">Aún no ha creado salidas</p>
            ) : (
              <div className="space-y-2">
                {recent.slice(0, 3).map((r) => {
                  const m = MODALITIES.find((mm) => mm.value === r.modality);
                  const mColor = r.modality === "road" ? "#F5C518" : r.modality === "gravel" ? "#e07b3a" : "#4caf7d";
                  return (
                    <Link
                      key={r.id} to="/ride/$rideId" params={{ rideId: r.id }}
                      className="flex items-center justify-between rounded-xl bg-[#222] px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                            style={{ background: `color-mix(in oklab, ${mColor} 22%, transparent)`, color: mColor }}
                          >{m?.label}</span>
                          <span className="truncate text-xs text-white/70">{r.start_address?.split(",")[0] ?? "—"}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{relativeMinutes(r.start_time)}</div>
                      </div>
                      <div className="text-sm font-medium">{r.estimated_km ? `${r.estimated_km} km` : "—"}</div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Widget>

          <Widget icon={<Zap className="h-4 w-4 text-[#F5C518]" />} title="Watts Points">
            <div className="text-center py-2">
              <div
                className="text-[#F5C518] tabular-nums"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 44, lineHeight: 1, fontWeight: 700 }}
              >
                {profile.watts_points ?? 0} Watts
              </div>
            </div>
          </Widget>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function Widget({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-4">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <ChevronRight className={`h-4 w-4 text-white/50 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="p-3 md:p-4 text-center">
      <div className="font-display text-primary" style={{ fontSize: 20 }}>{n}</div>
      <div className="text-muted-foreground" style={{ fontSize: 10 }}>{label}</div>
    </div>
  );
}
